import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useMemo, useState } from "react";

import { practiceUsersApi } from "apis";
import CustomDataGrid from "components/common/CustomDataGrid";
import { EDefaultValues } from "constants/defaultValues";
import {
  BranchDto,
  ListPracticeUsersForAdminSortByEnum,
  ListPracticeUsersForAdminSortOrderEnum,
} from "libs/cellera-sdk";
import { v4 as uuidv4 } from "uuid";

interface PracticeUserBranchesSectionProps {
  branches: BranchDto[];
  minHeight?: number;
  onNavigateToUser?: (userId: string) => void;
}

type BranchStaffUser = {
  id: string;
  name: string;
  email: string;
  role?: string;
  status?: string;
};

type BranchRow = {
  id: string;
  address: string;
  emailAddress: string;
  primaryPhoneNumber: string;
  faxNumber?: string;
  staffCount: number;
  branchData: BranchDto;
};

const PracticeUserBranchesSection: React.FC<
  PracticeUserBranchesSectionProps
> = ({ branches, minHeight = 150, onNavigateToUser }) => {
  const [staffModal, setStaffModal] = useState<{
    open: boolean;
    branch?: BranchDto;
    users: BranchStaffUser[];
    loading: boolean;
    error?: string;
  }>({
    open: false,
    users: [],
    loading: false,
  });

  const closeStaffModal = useCallback(() => {
    setStaffModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleUserSelection = useCallback(
    (userId: string) => {
      if (!userId) {
        return;
      }
      closeStaffModal();
      onNavigateToUser?.(userId);
    },
    [closeStaffModal, onNavigateToUser],
  );

  const loadBranchStaff = useCallback(async (branch: BranchDto) => {
    setStaffModal({
      open: true,
      branch,
      users: [],
      loading: Boolean(branch.id),
      error: branch.id ? undefined : "Branch identifier is missing.",
    });

    if (!branch.id) {
      return;
    }

    try {
      const response = await practiceUsersApi.listPracticeUsersForAdmin({
        branchId: branch.id,
        page: 1,
        pageSize: 100,
        sortBy: ListPracticeUsersForAdminSortByEnum.Name,
        sortOrder: ListPracticeUsersForAdminSortOrderEnum.Asc,
      });

      const users: BranchStaffUser[] =
        response.practiceUsers?.map((user) => ({
          id: user.id ?? "",
          name: user.name ?? EDefaultValues.EMPTY_STRING,
          email: user.email ?? EDefaultValues.EMPTY_STRING,
          role: user.role ?? undefined,
          status: user.status ?? undefined,
        })) || [];

      setStaffModal((prev) => ({
        ...prev,
        users,
        loading: false,
        error: undefined,
      }));
    } catch (error) {
      setStaffModal((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : "Unable to load branch staff.",
      }));
    }
  }, []);

  const columns: GridColDef<BranchRow>[] = useMemo(
    () => [
      {
        field: "address",
        headerName: "Address",
        flex: 1,
        renderCell: (params) => {
          return params.value || EDefaultValues.EMPTY_STRING;
        },
      },
      {
        field: "emailAddress",
        headerName: "Email Address",
        flex: 1,
        renderCell: (params) => {
          return params.value || EDefaultValues.EMPTY_STRING;
        },
      },
      {
        field: "primaryPhoneNumber",
        headerName: "Primary Phone Number",
        flex: 1,
        renderCell: (params) => {
          return params.value || EDefaultValues.EMPTY_STRING;
        },
      },
      {
        field: "faxNumber",
        headerName: "Fax Number",
        flex: 1,
        renderCell: (params) => {
          return params.value || EDefaultValues.EMPTY_STRING;
        },
      },
      {
        field: "staffCount",
        headerName: "Staff",
        width: 140,
        renderCell: (params) => {
          const branch = params.row.branchData;
          const count = params.value ?? 0;
          const isClickable = Boolean(branch.id);
          return (
            <Button
              variant="text"
              size="small"
              disabled={!isClickable}
              onClick={(event) => {
                event.stopPropagation();
                loadBranchStaff(branch);
              }}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                minWidth: 0,
              }}
            >
              {count}
            </Button>
          );
        },
      },
    ],
    [loadBranchStaff],
  );

  const rows = useMemo(
    () =>
      branches.map((branch) => ({
        id: branch.id || uuidv4(),
        address: branch.address,
        emailAddress: branch.emailAddress,
        primaryPhoneNumber: branch.primaryPhoneNumber,
        faxNumber: branch.faxNumber,
        staffCount: branch.staffCount ?? 0,
        branchData: branch,
      })),
    [branches],
  );

  const renderStaffDialogContent = () => {
    if (staffModal.loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (staffModal.error) {
      return <Alert severity="error">{staffModal.error}</Alert>;
    }

    if (!staffModal.users.length) {
      return (
        <Typography sx={{ py: 2, color: "#6B7280" }}>
          No staff members are assigned to this branch yet.
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {staffModal.users.map((user) => (
            <TableRow
              key={user.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() => handleUserSelection(user.id)}
            >
              <TableCell>{user.name || "-"}</TableCell>
              <TableCell>{user.email || "-"}</TableCell>
              <TableCell>
                {user.role
                  ? user.role.replace("_", " ").toUpperCase()
                  : EDefaultValues.EMPTY_STRING}
              </TableCell>
              <TableCell sx={{ textTransform: "capitalize" }}>
                {user.status || EDefaultValues.EMPTY_STRING}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 4, mb: 4 }}
    >
      <Typography variant="h6" sx={{ fontSize: "1.25rem", fontWeight: 500 }}>
        Branches
      </Typography>
      <Box sx={{ position: "relative", width: "100%" }}>
        <CustomDataGrid
          rows={rows}
          columns={columns}
          minHeight={minHeight}
          localeText={{ noRowsLabel: "No branch for this practice found" }}
        />
      </Box>

      <Dialog
        open={staffModal.open}
        onClose={closeStaffModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 5 }}>
          Branch Staff
          <Typography
            variant="body2"
            sx={{ color: "#6B7280", fontWeight: 400, mt: 0.5 }}
          >
            {staffModal.branch?.address || "Branch details"}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={closeStaffModal}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderStaffDialogContent()}</DialogContent>
      </Dialog>
    </Box>
  );
};

export default PracticeUserBranchesSection;
